/**
 * deriveTopology — THE single policy for deriving an indication's topology
 * from its geometry. Every surface that fills station/inverter/mppt/dccb/
 * string/module routes through here; per-surface merge policies are the bug
 * this module exists to delete.
 *
 * Policy source: typology session (c6) spec 2026-09-02 + owner decisions of
 * the same day:
 *  - STORED values stay authoritative for existing, un-moved indications;
 *    surfaces show a "grid says X" affordance where the derive disagrees.
 *    This module only DERIVES — it never decides what to overwrite.
 *  - Every derived value carries PROVENANCE (layoutId, gridSchemaVersion,
 *    config hash) so 'derived' can later be silently re-derived when a grid
 *    improves, while human-typed values are never displaced.
 *
 * Mechanics (each rule is load-bearing; see the spec for the audit numbers):
 *  a. Layout resolution: the area contest runs across EVERY layout with a
 *     transform — never activeLayoutId (T-09).
 *  b. Candidates: generateModuleCellsNearGeometry per layout, then per-cell
 *     intersection area. A cell is significant when the overlap is ≥5% of
 *     the CELL's area (cell-normalized: a grazing corner never counts, a
 *     fully-covered cell inside a String OC always does).
 *  c. Winner: argmax by area with a DETERMINISTIC order (area desc,
 *     entityId asc, moduleNumber asc). 27 bit-identical ties exist in
 *     production; `ambiguous` is set when the top-two margin is under 2% of
 *     the winner's cell area so the UI can show doubt instead of coin-flip.
 *  d. Single vs string: >4 significant cells OR markerArea/cellArea ≥ 1.5
 *     ⇒ string mode (no module; parent = greatest TOTAL overlap). The ratio
 *     measured 1.00 for every single-module case and 10.00 for every
 *     String OC in the audit, at every rotation.
 *  e. Hierarchy: from the winning cell's entity via effectiveClassification;
 *     absent hierarchy → classifyLabel(value, layout.classificationConfig)
 *     (config is NOT optional — T-08). Fields the hierarchy does not define
 *     stay EMPTY — never topped up from nearby labels (T-07).
 *  f. No grid anywhere / marker outside every grid: label-proximity result,
 *     explicitly labeled kind:'proximity' (T-13) so no surface presents a
 *     guess as a grid answer. Never a silent fallback: `offGrid` marks a
 *     grid site whose marker overlapped nothing.
 */

import type {
  SiteLayout,
  LayoutTextEntity,
  TopologyFields,
  TopologyLevel,
} from '../layout/types';
import { effectiveClassification, classifyLabel } from '../layout/classify';
import { generateModuleCellsNearGeometry } from './moduleGrid';
import {
  buildSpatialIndex,
  suggestTopology,
  extractHierarchyFields,
  stripFieldPrefix,
  TOPOLOGY_LEVELS,
  FIELD_PREFIX_TOKENS,
} from './suggest';
import { numericFeatureId } from '../../utils/featureIds';

// ── Tunables (from the audit; change only with a re-run of the detector) ──

/** significant cell: overlap ≥ this fraction OF THE CELL's area */
const CELL_SIGNIFICANCE_FLOOR = 0.05;
/** ambiguous: top-two margin under this fraction of the winner's cell area */
const AMBIGUOUS_MARGIN = 0.02;
/**
 * String mode ⟺ markerArea / cellArea > this — the AREA ratio alone, the
 * owner's "covers more than 4 modules" taken literally. The audit's three
 * populations: genuine String OC 10–28, correct single module ~1.0,
 * MIS-sized single-module marker (T-14) 2.86 — so 4 separates them and 1.5
 * (the first spec) would misclassify the mis-sized ones. A significant-cell
 * COUNT is deliberately NOT part of the decision: overlapping cells inflate
 * a count but cannot inflate an area (47 single-module marks at Bnei israel
 * touch >4 overlapping cells at ratio 1.0). The count ships as a DIAGNOSTIC
 * on the result instead.
 */
const STRING_MODE_AREA_RATIO = 4;

// ── Result shape ─────────────────────────────────────────────

/** Where a derived value came from — stamped into provenance so 'derived'
 *  fields can be silently re-derived when the grid improves, while
 *  human-typed values are never displaced (owner decision 2026-09-02). */
export interface DeriveSource {
  layoutId: string;
  gridSchemaVersion: number;
  /** hash of the grid config + the winning string's override — a changed
   *  hash means the geometry that produced the value no longer exists */
  configHash: number;
}

export interface GridDeriveResult {
  kind: 'grid';
  fields: Partial<TopologyFields>;
  customFields?: Record<string, string>;
  source: DeriveSource;
  cell: {
    entityId: string;
    moduleNumber: number;
    totalModules: number;
    stringLabel: string;
  };
  /** marker covers the string (String OC etc.) — fields carry NO module */
  isString: boolean;
  /** winner overlap / winner cell area (0..1] */
  overlapFraction: number;
  /** top-two margin / winner cell area; 1 when unopposed */
  marginFraction: number;
  ambiguous: boolean;
  /** DIAGNOSTIC: significant cells in the winner's layout. Never part of the
   *  string/single decision (overlapping cells double-count); a count far
   *  above markerArea/cellArea means the grid has overlapping cells HERE —
   *  surface it, don't act on it. */
  significantCellCount: number;
  /** The winning entity's label was NOT a plausible string identifier (the
   *  2026-08-24 label-merge audit counted 624 glued/merged/phantom texts
   *  across 15 of 16 layout sites). The string field is left EMPTY rather
   *  than shipping "14-1120-10" to CSVs and reports; the raw text rides
   *  here as a grid-quality signal for the Module Grid panel. */
  unparseableLabel?: string;
  /** Hierarchy captures the field guard dropped (e.g. MPPT "INV" — a schema
   *  segment capturing a literal prefix, or Evron's "3DCCB" in mppt). The
   *  field stays EMPTY; the raw value rides here as a schema-quality signal.
   *  Exposure-checked 2026-09-02: 16 rejects fleet-wide, all genuine junk;
   *  composite values carrying their own field token ("2.DCCB11") pass. */
  rejectedFields?: Record<string, string>;
}

export interface ProximityDeriveResult {
  kind: 'proximity';
  fields: Partial<TopologyFields>;
  customFields?: Record<string, string>;
  layoutId: string;
  confidence: number;
  explanation: string;
  /** grid layouts exist but the marker overlapped no cell — the value is a
   *  label guess on a gridded site, surfaces must say so */
  offGrid: boolean;
}

export interface UnknownDeriveResult {
  kind: 'unknown';
  reason: 'no-layout' | 'bad-geometry' | 'no-labels';
}

export type DeriveResult = GridDeriveResult | ProximityDeriveResult | UnknownDeriveResult;

// ── Local-meter geometry ─────────────────────────────────────

function toLocal(
  lng: number, lat: number, oLng: number, oLat: number, mpdLng: number, mpdLat: number,
): [number, number] {
  return [(lng - oLng) * mpdLng, (lat - oLat) * mpdLat];
}

function shoelace(ring: [number, number][]): number {
  let s = 0;
  const n = ring.length;
  for (let i = 0; i < n; i++) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[(i + 1) % n];
    s += x1 * y2 - x2 * y1;
  }
  return s;
}

function ringArea(ring: [number, number][]): number {
  return Math.abs(shoelace(ring)) / 2;
}

/** Sutherland–Hodgman: area of subject ∩ clip (clip forced counter-clockwise).
 *  Same algorithm as the audit's phase2_replay.clip_area — results comparable. */
function clipArea(subject: [number, number][], clip: [number, number][]): number {
  let out = [...subject];
  const cc = shoelace(clip) < 0 ? [...clip].reverse() : [...clip];
  const n = cc.length;
  for (let i = 0; i < n; i++) {
    if (out.length === 0) return 0;
    const [ax, ay] = cc[i];
    const [bx, by] = cc[(i + 1) % n];
    const side = (p: [number, number]) => (bx - ax) * (p[1] - ay) - (by - ay) * (p[0] - ax);
    const next: [number, number][] = [];
    for (let j = 0; j < out.length; j++) {
      const P = out[j];
      const Q = out[(j + 1) % out.length];
      const dP = side(P);
      const dQ = side(Q);
      if (dP >= 0) next.push(P);
      if ((dP >= 0) !== (dQ >= 0)) {
        const t = dP / (dP - dQ);
        next.push([P[0] + t * (Q[0] - P[0]), P[1] + t * (Q[1] - P[1])]);
      }
    }
    out = next;
  }
  return out.length ? ringArea(out) : 0;
}

function pointInRing(x: number, y: number, ring: [number, number][]): boolean {
  let inside = false;
  const n = ring.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

/** marker outline in lng/lat, or null for non-areal geometry (Point ⇒ []) */
function markerRing(geometry: GeoJSON.Geometry): [number, number][] | null {
  if (geometry.type === 'Polygon') {
    const ring = geometry.coordinates[0] as [number, number][];
    if (!ring || ring.length < 3) return null;
    // drop the closing vertex — the clip walks edges cyclically
    const open = ring.length > 3
      && ring[0][0] === ring[ring.length - 1][0]
      && ring[0][1] === ring[ring.length - 1][1]
      ? ring.slice(0, -1)
      : ring;
    return open.map((c) => [c[0], c[1]]);
  }
  if (geometry.type === 'Point') return [];
  return null;
}

// ── The derive ───────────────────────────────────────────────

interface Candidate {
  layout: SiteLayout;
  entityId: string;
  moduleNumber: number;
  totalModules: number;
  stringLabel: string;
  overlapM2: number;
  cellAreaM2: number;
  /** cell outline in the marker's local-meter frame (center-preference test) */
  cellRingLocal: [number, number][];
}

export interface DeriveOptions {
  /** The indication's TYPE. 'String OC' is a declaration that the fault is
   *  string-level — it forces string mode instead of re-litigating it from
   *  marker area (whose default size is a user setting: at
   *  defaultStringModules=3 a genuine String OC ratio drops to ~2.3-3.0 and
   *  the area test alone would hand it a module number). */
  indicationType?: string;
}

/** Types whose meaning is "the whole string" — never a single module. */
const STRING_LEVEL_TYPES = new Set(['String OC']);

export function deriveTopology(
  layouts: SiteLayout[],
  geometry: GeoJSON.Geometry,
  options?: DeriveOptions,
): DeriveResult {
  const usable = layouts.filter((l) => l?.transform);
  if (usable.length === 0) return { kind: 'unknown', reason: 'no-layout' };

  const ring = markerRing(geometry);
  if (ring === null) {
    // LineString etc. — no areal contest; try proximity from any coordinate
    return proximityAcross(usable, geometry, false);
  }

  const gridLayouts = usable.filter((l) => l.moduleGridConfig);
  if (gridLayouts.length === 0) return proximityAcross(usable, geometry, false);

  // Shared local frame: all areas compare in the same meters
  const anchor = ring.length > 0 ? ring[0] : (geometry as GeoJSON.Point).coordinates as [number, number];
  const [oLng, oLat] = anchor;
  const mpdLat = 111320;
  const mpdLng = 111320 * Math.cos((oLat * Math.PI) / 180);
  const markerLocal = ring.map(([lng, lat]) => toLocal(lng, lat, oLng, oLat, mpdLng, mpdLat));
  const markerAreaM2 = markerLocal.length >= 3 ? ringArea(markerLocal) : 0;
  const isPoint = ring.length === 0;

  // (a)+(b) — contest across every grid layout, cell-normalized floor
  const candidates: Candidate[] = [];
  for (const layout of gridLayouts) {
    const fc = generateModuleCellsNearGeometry(layout, geometry);
    for (const f of fc.features) {
      if (f.geometry.type !== 'Polygon') continue;
      const cellRing = (f.geometry.coordinates[0] as [number, number][])
        .slice(0, -1)
        .map(([lng, lat]) => toLocal(lng, lat, oLng, oLat, mpdLng, mpdLat));
      const cellAreaM2 = ringArea(cellRing);
      if (cellAreaM2 <= 0) continue;
      let overlapM2: number;
      if (isPoint) {
        const [px, py] = toLocal(anchor[0], anchor[1], oLng, oLat, mpdLng, mpdLat);
        overlapM2 = pointInRing(px, py, cellRing) ? cellAreaM2 : 0;
      } else {
        overlapM2 = clipArea(markerLocal, cellRing);
      }
      if (overlapM2 < CELL_SIGNIFICANCE_FLOOR * cellAreaM2) continue;
      const p = f.properties!;
      candidates.push({
        layout,
        entityId: p.entityId,
        moduleNumber: p.moduleNumber,
        totalModules: p.totalModules,
        stringLabel: p.stringLabel,
        overlapM2,
        cellAreaM2,
        cellRingLocal: cellRing,
      });
    }
  }

  if (candidates.length === 0) {
    // Gridded site, marker on none of its cells — a labeled guess, never a
    // silent one (the audit counted 2,073 of these).
    return proximityAcross(usable, geometry, true);
  }

  // (c) — deterministic winner
  candidates.sort((a, b) =>
    b.overlapM2 - a.overlapM2
    || (a.entityId < b.entityId ? -1 : a.entityId > b.entityId ? 1 : 0)
    || a.moduleNumber - b.moduleNumber,
  );
  const winner = candidates[0];
  const runnerUp = candidates[1];
  const marginFraction = runnerUp
    ? (winner.overlapM2 - runnerUp.overlapM2) / winner.cellAreaM2
    : 1;

  // (d) — single module vs whole string: a string-level TYPE declares it;
  // otherwise the AREA ratio alone (see the STRING_MODE_AREA_RATIO doc for
  // why the cell count must not vote)
  const isString =
    (options?.indicationType !== undefined && STRING_LEVEL_TYPES.has(options.indicationType))
    || (!isPoint && markerAreaM2 / winner.cellAreaM2 > STRING_MODE_AREA_RATIO);

  // String mode: parent = the string whose cell contains the marker's
  // CENTER — the click is the strongest statement of intent, and it makes a
  // misoriented String OC rect harmless (a broadside 28m rect crosses 5-8
  // strings with similar per-string overlaps; greatest-total alone would be
  // decided by noise — c6 review of 0960e31). Greatest TOTAL overlap decides
  // only among center-containing strings, and is the fallback when the
  // center sits on no cell. T-14 (rect oriented from its cell) makes this
  // belt-and-braces.
  let parent = winner;
  if (isString) {
    let cx = 0, cy = 0;
    for (const [x, y] of markerLocal) { cx += x; cy += y; }
    cx /= markerLocal.length;
    cy /= markerLocal.length;
    const centerOwners = new Set(
      candidates
        .filter((c) => pointInRing(cx, cy, c.cellRingLocal))
        .map((c) => `${c.layout.id} ${c.entityId}`),
    );

    const totals = new Map<string, { c: Candidate; sum: number }>();
    for (const c of candidates) {
      const key = `${c.layout.id} ${c.entityId}`;
      const t = totals.get(key);
      if (t) t.sum += c.overlapM2;
      else totals.set(key, { c, sum: c.overlapM2 });
    }
    const pool = centerOwners.size > 0
      ? [...totals.entries()].filter(([k]) => centerOwners.has(k)).map(([, v]) => v)
      : [...totals.values()];
    let best: { c: Candidate; sum: number } | null = null;
    for (const t of pool) {
      if (!best || t.sum > best.sum
        || (t.sum === best.sum && t.c.entityId < best.c.entityId)) {
        best = t;
      }
    }
    parent = best!.c;
  }

  // (e) — hierarchy strictly from the winning string's entity
  const layout = parent.layout;
  const entity = entityById(layout).get(parent.entityId);
  const { fields, customFields, unparseableLabel, rejectedFields } =
    fieldsFromEntity(entity, parent.stringLabel, layout);
  if (!isString) fields.module = String(winner.moduleNumber);

  const cfg = layout.moduleGridConfig!;
  const override = layout.stringOverrides?.[parent.entityId];
  return {
    kind: 'grid',
    fields,
    ...(customFields && Object.keys(customFields).length > 0 ? { customFields } : {}),
    source: {
      layoutId: layout.id,
      gridSchemaVersion: cfg.gridSchemaVersion ?? 1,
      configHash: numericFeatureId(stableStringify({ cfg, override: override ?? null })),
    },
    cell: {
      entityId: parent.entityId,
      moduleNumber: winner.moduleNumber,
      totalModules: parent.totalModules,
      stringLabel: parent.stringLabel,
    },
    isString,
    overlapFraction: Math.min(1, winner.overlapM2 / winner.cellAreaM2),
    marginFraction,
    ambiguous: runnerUp !== undefined && marginFraction < AMBIGUOUS_MARGIN,
    // scoped to the winner's LAYOUT: a second sheet covering the same ground
    // (MST Shorek) must not inflate the diagnostic
    significantCellCount: candidates.filter((c) => c.layout === winner.layout).length,
    ...(unparseableLabel !== undefined ? { unparseableLabel } : {}),
    ...(rejectedFields ? { rejectedFields } : {}),
  };
}

/** JSON with recursively sorted keys — the same grid config must hash the
 *  same whether built in code or parsed back from R2 (key order differs). */
function stableStringify(v: unknown): string {
  if (v === null || typeof v !== 'object') return JSON.stringify(v);
  if (Array.isArray(v)) return `[${v.map(stableStringify).join(',')}]`;
  const keys = Object.keys(v as Record<string, unknown>).sort();
  return `{${keys
    .map((k) => `${JSON.stringify(k)}:${stableStringify((v as Record<string, unknown>)[k])}`)
    .join(',')}}`;
}

// id→entity per layout, memoized on the layout OBJECT (layouts are replaced,
// not mutated, on change) — the chip runs a derive per displayed indication
// and Nir itzhak has thousands of text entities (review item 4).
const entityMapCache = new WeakMap<SiteLayout, Map<string, LayoutTextEntity>>();

function entityById(layout: SiteLayout): Map<string, LayoutTextEntity> {
  let m = entityMapCache.get(layout);
  if (!m) {
    m = new Map(layout.textEntities.map((t) => [t.id, t]));
    entityMapCache.set(layout, m);
  }
  return m;
}

/**
 * A plausible STRING IDENTIFIER after prefix stripping: short, an optional
 * letter prefix, 1-4 digits, up to two dot/dash-joined digit groups, an
 * optional half-string letter suffix. Covers the production shapes: "17",
 * "02", "STR16"→"16", "7.7A" (HSN half-strings), "1.18.12" (GHS chains).
 * Rejects glued merge products ("14-1120-10", "09.0709.06") mostly via the
 * 8-char cap — a glued label is two identifiers long. An honest empty beats
 * a confident wrong; rejects surface as unparseableLabel.
 */
const STRING_IDENTIFIER_RE = /^[A-Za-z]{0,6}\d{1,4}([.\-]\d{1,4}){0,2}[A-Za-z]{0,2}$/;
const STRING_IDENTIFIER_MAX_LEN = 8;

/** Is this bare value a plausible string identifier? Exported for the
 *  Module Grid panel's phantom-grid census: a STRING-level entity whose
 *  value fails this shape is very likely a mis-classified header ("2.DCCB2"
 *  stamping 36 phantom cells at a combiner box) or merge glue. */
export function stringIdentifierOk(bare: string): boolean {
  return bare.length <= STRING_IDENTIFIER_MAX_LEN && STRING_IDENTIFIER_RE.test(bare);
}

/**
 * Field-value guard for hierarchy captures (c6 review, exposure-checked):
 * a value is written to a standard topology field only when it is either a
 * plausible identifier after the anchored prefix strip, OR a composite
 * carrying its OWN field's token ("2.DCCB11" in dccb, "72ST1" in station —
 * 493 legitimate composite DCCBs exist at one site alone). "INV" in mppt
 * (no digits) and "3DCCB" in mppt (foreign token) both fail. False-admits
 * reproduce today's behavior; false-rejects visibly empty a field — so the
 * guard errs wide.
 */
function fieldValueOk(field: string, raw: string): boolean {
  const bare = stripFieldPrefix(field, raw);
  if (stringIdentifierOk(bare)) return true;
  const tokens = FIELD_PREFIX_TOKENS[field];
  if (tokens) {
    const composite = new RegExp(`^[A-Za-z0-9]{1,4}[.\\-]?(?:${tokens})\\d{1,4}[A-Za-z]{0,2}$`, 'i');
    if (composite.test(raw.trim())) return true;
  }
  return false;
}

/** Hierarchy from the entity's governing classification; fields the
 *  hierarchy does not define stay EMPTY (T-07 — no proximity top-ups). */
function fieldsFromEntity(
  entity: LayoutTextEntity | undefined,
  stringLabel: string,
  layout: SiteLayout,
): {
  fields: Partial<TopologyFields>;
  customFields?: Record<string, string>;
  unparseableLabel?: string;
  rejectedFields?: Record<string, string>;
} {
  const fields: Partial<TopologyFields> = {};
  const customFields: Record<string, string> = {};
  const cls = entity ? effectiveClassification(entity) : undefined;
  const value = cls?.value ?? stringLabel;

  const hierarchy = cls
    ? extractHierarchyFields(cls, layout.classificationConfig)
    : classifyLabel(value, layout.classificationConfig)?.hierarchy ?? null;

  const rejectedFields: Record<string, string> = {};
  if (hierarchy && Object.keys(hierarchy).length > 0) {
    for (const [key, v] of Object.entries(hierarchy)) {
      if (!v || key === 'skip') continue;
      if (TOPOLOGY_LEVELS.includes(key as TopologyLevel)) {
        if (fieldValueOk(key, v)) {
          fields[key as keyof TopologyFields] = stripFieldPrefix(key, v);
        } else {
          rejectedFields[key] = v; // honest empty beats a confident wrong
        }
      } else {
        // custom fields (Table, Input, …) are NOT identifier-shaped — never
        // guarded ("YA"/"YB" are legitimate table ids)
        customFields[key] = v;
      }
    }
    // A hierarchy that defines no string key leaves `string` EMPTY — the
    // raw value here is the WHOLE label ("1.1.DCCB03.STR16"), and a field
    // meaning "string number" must never carry it (review item 5).
  } else {
    // No hierarchy at all: the value SHOULD be a bare identifier ("17") —
    // but merge-corrupted labels also land here, and they must not reach
    // the string field (see STRING_IDENTIFIER_RE).
    const bare = stripFieldPrefix('string', value);
    if (stringIdentifierOk(bare)) {
      fields.string = bare;
    } else {
      return {
        fields,
        ...(Object.keys(customFields).length > 0 ? { customFields } : {}),
        unparseableLabel: value,
      };
    }
  }
  return {
    fields,
    ...(Object.keys(customFields).length > 0 ? { customFields } : {}),
    ...(Object.keys(rejectedFields).length > 0 ? { rejectedFields } : {}),
  };
}

/** (f) — label proximity across every layout with a transform, best
 *  confidence wins. Explicitly labeled; the T-07/T-11/T-12 fixes to the
 *  proximity internals land as their own reviewed change to suggest.ts. */
function proximityAcross(
  layouts: SiteLayout[],
  geometry: GeoJSON.Geometry,
  offGrid: boolean,
): ProximityDeriveResult | UnknownDeriveResult {
  const center = centerOf(geometry);
  if (!center) return { kind: 'unknown', reason: 'bad-geometry' };

  let best: { s: NonNullable<ReturnType<typeof suggestTopology>>; layoutId: string } | null = null;
  for (const layout of layouts) {
    const index = buildSpatialIndex(layout);
    if (!index) continue;
    const s = suggestTopology(center[0], center[1], index, layout);
    if (s && (!best || s.confidence > best.s.confidence)) {
      best = { s, layoutId: layout.id };
    }
  }
  if (!best) return { kind: 'unknown', reason: 'no-labels' };
  return {
    kind: 'proximity',
    fields: best.s.fields,
    ...(best.s.customFields ? { customFields: best.s.customFields } : {}),
    layoutId: best.layoutId,
    confidence: best.s.confidence,
    explanation: best.s.explanation,
    offGrid,
  };
}

function centerOf(geometry: GeoJSON.Geometry): [number, number] | null {
  if (geometry.type === 'Point') {
    return [geometry.coordinates[0], geometry.coordinates[1]];
  }
  if (geometry.type === 'Polygon') {
    const ring = geometry.coordinates[0] as [number, number][];
    if (!ring || ring.length === 0) return null;
    const n = ring.length > 1
      && ring[0][0] === ring[ring.length - 1][0]
      && ring[0][1] === ring[ring.length - 1][1]
      ? ring.length - 1 : ring.length;
    let sx = 0, sy = 0;
    for (let i = 0; i < n; i++) { sx += ring[i][0]; sy += ring[i][1]; }
    return [sx / n, sy / n];
  }
  return null;
}
